import { clamp, inRect, lerp, smoothstep } from '../core/math';
import type { TerrainData } from '../data/types';
import type { Context, Grid, Site } from './types';

type GradeInput = Pick<Site, 'FH' | 'yRef' | 'pit' | 'blocks'>;

/** 周辺 1km 地形の高さ（絶対標高）。格子の双線形補間で、外は端の値。 */
export function sampleContext(c: Context, x: number, z: number): number {
  const st = c.cw / c.cn;
  const fx = clamp((x + c.cw / 2) / st, 0, c.cn - 1e-4);
  const fz = clamp((z + c.cw / 2) / st, 0, c.cn - 1e-4);
  const i = Math.floor(fx);
  const j = Math.floor(fz);
  const u = fx - i;
  const v = fz - j;
  const W = c.cn + 1;
  const C = c.C;
  return lerp(
    lerp(C[j * W + i]!, C[j * W + i + 1]!, u),
    lerp(C[(j + 1) * W + i]!, C[(j + 1) * W + i + 1]!, u),
    v,
  );
}

/** すり付け前の計画高（基準高からの相対）。建物の根切りと調整池を反映する。 */
export function coreAt(s: GradeInput, x: number, z: number): number {
  const F = s.FH - s.yRef;
  let D = F;
  for (const b of s.blocks) {
    if (b.kind === 'bldg' && inRect(x, z, b.x, b.z, b.w + 2, b.d + 2))
      D = Math.min(D, F - s.pit * b.pitK);
    if (b.kind === 'water') {
      const di = Math.min(
        x - (b.x - b.w / 2),
        b.x + b.w / 2 - x,
        z - (b.z - b.d / 2),
        b.z + b.d / 2 - z,
      );
      if (di > 0) D = Math.min(D, Math.max(F - s.pit, F - di * 0.5));
    }
  }
  return D;
}

export function buildContext(t: TerrainData): Context {
  let cmin = Infinity;
  let cmax = -Infinity;
  for (const v of t.C) {
    if (v < cmin) cmin = v;
    if (v > cmax) cmax = v;
  }
  return { C: t.C, cn: t.cn, cw: t.cw, cmin, cmax };
}

/**
 * 現況の地面を作る。施工範囲の外周 8m の帯で、周辺地形（10m）から施工範囲（5m）の高さへ
 * なめらかに切りかえる。計画面は `buildDesign` で作る。
 */
export function buildGrid(
  t: TerrainData,
  ctx: Context,
  area: { w: number; d: number },
  yRef: number,
): Grid {
  const { w, d } = area;
  const { nx, nz } = t;
  const dx = w / nx;
  const dz = d / nz;
  const nv = (nx + 1) * (nz + 1);
  const E = new Float32Array(nv);
  const LX = new Float32Array(nv);
  const LZ = new Float32Array(nv);
  for (let j = 0; j <= nz; j++) {
    for (let i = 0; i <= nx; i++) {
      const k = j * (nx + 1) + i;
      const x = -w / 2 + i * dx;
      const z = -d / 2 + j * dz;
      const ed = Math.min(w / 2 - Math.abs(x), d / 2 - Math.abs(z));
      E[k] = lerp(sampleContext(ctx, x, z), t.E[k]!, smoothstep(ed / 8)) - yRef;
      LX[k] = x;
      LZ[k] = z;
    }
  }
  return {
    nx,
    nz,
    dx,
    dz,
    nv,
    E,
    D: new Float32Array(nv),
    LX,
    LZ,
    SL: new Float32Array(nv),
    colCut: new Float64Array(nx),
    colFill: new Float64Array(nx),
    Vc: 0,
    Vf: 0,
    minH: 0,
    maxH: 0,
  };
}

/**
 * 計画面 D・勾配・切土量 Vc・盛土量 Vf を、いまの計画地盤高と根切り深さから作り直す。
 * 土量はメッシュ法（四隅の高さ、切盛が混ざるマスは遷移セル補正）。
 */
export function buildDesign(
  s: Pick<Site, 'FH' | 'yRef' | 'pit' | 'blocks' | 'area' | 'kind' | 'g'>,
): void {
  const g = s.g;
  const { w, d } = s.area;
  const mD = s.kind.mD;
  const W = g.nx + 1;
  for (let k = 0; k < g.nv; k++) {
    const x = g.LX[k]!;
    const z = g.LZ[k]!;
    const ed = Math.min(w / 2 - Math.abs(x), d / 2 - Math.abs(z));
    g.D[k] = lerp(g.E[k]!, coreAt(s, x, z), smoothstep(ed / mD));
  }
  for (let j = 0; j <= g.nz; j++) {
    for (let i = 0; i <= g.nx; i++) {
      const k = j * W + i;
      const i0 = Math.max(0, i - 1);
      const i1 = Math.min(g.nx, i + 1);
      const j0 = Math.max(0, j - 1);
      const j1 = Math.min(g.nz, j + 1);
      g.SL[k] = Math.hypot(
        (g.D[j * W + i1]! - g.D[j * W + i0]!) / ((i1 - i0) * g.dx),
        (g.D[j1 * W + i]! - g.D[j0 * W + i]!) / ((j1 - j0) * g.dz),
      );
    }
  }
  const A = g.dx * g.dz;
  g.colCut = new Float64Array(g.nx);
  g.colFill = new Float64Array(g.nx);
  g.Vc = 0;
  g.Vf = 0;
  for (let j = 0; j < g.nz; j++) {
    for (let i = 0; i < g.nx; i++) {
      let P = 0;
      let N = 0;
      for (const k of [j * W + i, j * W + i + 1, (j + 1) * W + i, (j + 1) * W + i + 1]) {
        const v = g.E[k]! - g.D[k]!;
        if (v > 0) P += v;
        else N -= v;
      }
      if (P + N < 1e-9) continue;
      const c = ((A / 4) * P * P) / (P + N);
      const f = ((A / 4) * N * N) / (P + N);
      g.colCut[i] = g.colCut[i]! + c;
      g.colFill[i] = g.colFill[i]! + f;
      g.Vc += c;
      g.Vf += f;
    }
  }
  g.minH = Infinity;
  g.maxH = -Infinity;
  for (let k = 0; k < g.nv; k++) {
    g.minH = Math.min(g.minH, g.E[k]!, g.D[k]!);
    g.maxH = Math.max(g.maxH, g.E[k]!, g.D[k]!);
  }
}
